import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Server, 
  Database, 
  Globe, 
  FileCode, 
  Layers,
  Box,
  Clock,
  Shield,
  Mail,
  HardDrive,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminArchitecture() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/architecture-stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching architecture stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const StatCard = ({ icon: Icon, title, value, subtitle, color = 'text-primary' }) => (
    <div className="bg-muted/30 rounded-lg p-4 border border-border">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-lg bg-background ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <span className="text-sm font-medium text-muted-foreground">{title}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
    </div>
  );

  const architecture = {
    frontend: {
      name: 'Frontend',
      tech: 'React 18 + Tailwind CSS + Shadcn/UI',
      components: [
        { name: 'UI Components', path: 'components/ui/', desc: 'Shadcn/UI (Button, Dialog, Select, Calendar...)' },
        { name: 'Admin Components', path: 'components/admin/', desc: '11 modules (Backup, Security, Logs, Architecture, Export...)' },
        { name: 'Pages', path: 'pages/', desc: '15 pages (extranet, manager, admin)' },
        { name: 'Contextes', path: 'contexts/', desc: 'Auth, Language, Theme' },
      ]
    },
    backend: {
      name: 'Backend',
      tech: 'FastAPI + Python 3.11',
      components: [
        { name: 'API Router', path: 'server.py', desc: `${stats?.backend_lines || '~8500'} lignes, ${stats?.endpoints_count || '120'} endpoints` },
        { name: 'Auth JWT', path: 'server.py', desc: 'Tokens + rôles (collaborateur, manager, directeur)' },
        { name: 'Scheduler', path: 'APScheduler', desc: 'Tâches planifiées (backup quotidien)' },
        { name: 'Email', path: 'SMTP/Brevo', desc: 'Envoi emails transactionnels et mailing (CID signatures)' },
        { name: 'PDF Export', path: 'ReportLab', desc: 'Export planning (2 semaines/page, multilingue)' },
        { name: 'Excel Export', path: 'openpyxl', desc: 'Export complet collaborateur (Admin)' },
      ]
    },
    database: {
      name: 'Base de données',
      tech: 'MongoDB',
      collections: [
        { name: 'users', desc: 'Comptes utilisateurs et préférences' },
        { name: 'schedule', desc: 'Créneaux horaires planifiés' },
        { name: 'absences', desc: 'Demandes de congés/absences' },
        { name: 'overtime_hours', desc: 'Heures supplémentaires' },
        { name: 'leave_allocations', desc: 'Allocations congés annuels' },
        { name: 'login_logs', desc: 'Logs de connexion (succès/échecs)' },
        { name: 'system_config', desc: 'Configuration système (SMTP, backup, sécurité)' },
        { name: 'events', desc: 'Événements du calendrier' },
        { name: 'site_pages_audit', desc: 'Audit du sitemap' },
        { name: 'mailing_*', desc: 'Contacts et campagnes email' },
      ]
    }
  };

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Architecture Technique
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Vue d'ensemble de l'infrastructure et des statistiques
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Statistics Cards */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            icon={Database} 
            title="Collections MongoDB" 
            value={stats.collections_count || 0}
            subtitle={`${stats.total_documents || 0} documents total`}
            color="text-emerald-600"
          />
          <StatCard 
            icon={FileCode} 
            title="Lignes Backend" 
            value={stats.backend_lines || 0}
            subtitle="Python (server.py, mailing.py)"
            color="text-blue-600"
          />
          <StatCard 
            icon={Globe} 
            title="Lignes Frontend" 
            value={stats.frontend_lines || 0}
            subtitle={stats.frontend_files ? `${stats.frontend_files} fichiers .js/.jsx` : 'Pages + Composants'}
            color="text-purple-600"
          />
          <StatCard 
            icon={Server} 
            title="Endpoints API" 
            value={stats.endpoints_count || 0}
            subtitle={stats.pages_count ? `${stats.pages_count} pages déclarées` : 'Routes REST'}
            color="text-amber-600"
          />
        </div>
      ) : null}

      {/* Architecture Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Frontend */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="bg-purple-500/10 border-b border-border p-4">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-purple-600" />
              <div>
                <h3 className="font-semibold">{architecture.frontend.name}</h3>
                <p className="text-xs text-muted-foreground">{architecture.frontend.tech}</p>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {architecture.frontend.components.map((comp, i) => (
              <div key={i} className="text-sm">
                <div className="font-medium">{comp.name}</div>
                <div className="text-xs text-muted-foreground">{comp.desc}</div>
                <code className="text-xs bg-muted px-1 rounded">{comp.path}</code>
              </div>
            ))}
          </div>
        </div>

        {/* Backend */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="bg-blue-500/10 border-b border-border p-4">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-blue-600" />
              <div>
                <h3 className="font-semibold">{architecture.backend.name}</h3>
                <p className="text-xs text-muted-foreground">{architecture.backend.tech}</p>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            {architecture.backend.components.map((comp, i) => (
              <div key={i} className="text-sm">
                <div className="font-medium">{comp.name}</div>
                <div className="text-xs text-muted-foreground">{comp.desc}</div>
                <code className="text-xs bg-muted px-1 rounded">{comp.path}</code>
              </div>
            ))}
          </div>
        </div>

        {/* Database */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="bg-emerald-500/10 border-b border-border p-4">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-emerald-600" />
              <div>
                <h3 className="font-semibold">{architecture.database.name}</h3>
                <p className="text-xs text-muted-foreground">{architecture.database.tech}</p>
              </div>
            </div>
          </div>
          <div className="p-4 space-y-2">
            {architecture.database.collections.map((col, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <code className="text-xs bg-muted px-2 py-0.5 rounded">{col.name}</code>
                <span className="text-xs text-muted-foreground">{col.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Collections Details */}
      {stats?.collections_details && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="bg-muted/50 border-b border-border p-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Database className="h-4 w-4" />
              Détail des Collections MongoDB
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {stats.collections_details.map((col, i) => (
                <div key={i} className="bg-muted/30 rounded p-3 text-sm">
                  <div className="font-mono text-xs text-primary">{col.name}</div>
                  <div className="text-lg font-bold">{col.count}</div>
                  <div className="text-xs text-muted-foreground">documents</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tech Stack Summary */}
      <div className="bg-muted/30 rounded-lg p-4 border border-border">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Box className="h-4 w-4" />
          Stack Technique
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground text-xs uppercase mb-1">Frontend</div>
            <div>React 18, Tailwind CSS, Shadcn/UI</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs uppercase mb-1">Backend</div>
            <div>FastAPI, Python 3.11, Pydantic</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs uppercase mb-1">Database</div>
            <div>MongoDB, Motor (async)</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs uppercase mb-1">Services</div>
            <div>Brevo (email), APScheduler</div>
          </div>
        </div>
      </div>

      {/* Features List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Sécurité
          </h3>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• Authentification JWT avec rôles</li>
            <li>• CAPTCHA Cloudflare Turnstile</li>
            <li>• Whitelist IP configurable</li>
            <li>• Logs de connexion</li>
            <li>• Validation des entrées (Pydantic)</li>
          </ul>
        </div>
        <div className="bg-muted/30 rounded-lg p-4 border border-border">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Fonctionnalités Clés
          </h3>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• Gestion planning multi-utilisateurs</li>
            <li>• Calcul automatique des heures</li>
            <li>• Export PDF et iCal</li>
            <li>• Sauvegarde automatique par email</li>
            <li>• Mailing intégré (Brevo)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
